import React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';

// Prefer env var, fallback to provided key (replace in production env)
const BUGSNAG_API_KEY = import.meta.env.VITE_BUGSNAG_API_KEY || '03665b54aa52e01a2ddd8d18fae6aa3e';
const RELEASE_STAGE = import.meta.env.MODE;
const APP_VERSION = import.meta.env.VITE_APP_VERSION;
const DEBUG = import.meta.env.VITE_DEBUG === 'true' || RELEASE_STAGE !== 'production';

// Start Bugsnag as early as possible
try {
    Bugsnag.start({
        apiKey: BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginReact()],
        releaseStage: RELEASE_STAGE,
        appVersion: APP_VERSION,
        enabledReleaseStages: ['production', 'staging', 'preview'],
        autoTrackSessions: true,
        // Capture useful breadcrumbs and redact sensitive keys
        enabledBreadcrumbTypes: ['error', 'log', 'navigation', 'request', 'state', 'user'],
        redactedKeys: [/authorization/i, /password/i, /api[-_]?key/i, /token/i, 'VITE_SUPABASE_ANON_KEY'],
        onError: (event) => {
            // Attach lightweight runtime context to every event
            try {
                event.addMetadata('context', {
                    path: window.location.pathname,
                    country: localStorage.getItem('selectedCountry') || 'US',
                    language: navigator.language,
                });
                if (APP_VERSION) {
                    event.addMetadata('build', { appVersion: APP_VERSION });
                }
            } catch {
                // ignore
            }
        },
    });

    // Start Performance monitoring (Web Vitals + resource timings)
    BugsnagPerformance.start({ apiKey: BUGSNAG_API_KEY });
} catch (e) {
    // Do not throw on init errors; just log
    // eslint-disable-next-line no-console
    console.warn('Bugsnag initialization failed:', e);
}

// Optional: instrument console to leave breadcrumbs when debugging
(function instrumentConsole() {
    if (!DEBUG) return;
    const w = window as any;
    if (w.__bugsnagConsolePatched) return;
    w.__bugsnagConsolePatched = true;

    const orig = {
        log: console.log.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        info: console.info.bind(console),
    };

    const leave = (level: string, args: any[]) => {
        try {
            Bugsnag.leaveBreadcrumb(`console.${level}` as any, { message: args.map(String).join(' ') }, 'log');
        } catch {/* noop */ }
    };

    console.log = (...args: any[]) => { leave('log', args); orig.log(...args); };
    console.info = (...args: any[]) => { leave('info', args); orig.info(...args); };
    console.warn = (...args: any[]) => { leave('warn', args); orig.warn(...args); };
    console.error = (...args: any[]) => { leave('error', args); orig.error(...args); };
})();

// Dev helpers exposed to the console for quick verification
if (DEBUG) {
    (window as any).Bugsnag = Bugsnag;
    (window as any).__bugsnagTest = () => Bugsnag.notify(new Error('Test error'));
    (window as any).__bugsnagPerfTest = async () => {
        const start = performance.now();
        // Simulate work
        await new Promise((r) => setTimeout(r, 120));
        const duration = performance.now() - start;
        Bugsnag.leaveBreadcrumb('perf:test', { durationMs: Math.round(duration) }, 'navigation');
        // Trigger a small network request to generate performance telemetry
        try {
            await fetch('/vite.svg', { cache: 'no-store' }).catch(() => { });
        } catch {/* ignore */ }
        return duration;
    };
}

// Export a React ErrorBoundary. If plugin unavailable, fall back to Fragment so app still renders.
const reactPlugin = Bugsnag.getPlugin('react') as any;
export const ErrorBoundary: React.ComponentType<{ children?: React.ReactNode }> =
    reactPlugin?.createErrorBoundary?.(React) || (({
        children,
    }: { children?: React.ReactNode }) => React.createElement(React.Fragment, null, children));

export { Bugsnag };

// Optional types for window helpers
declare global {
    interface Window {
        Bugsnag?: typeof Bugsnag;
        __bugsnagTest?: () => void;
        __bugsnagPerfTest?: () => Promise<number>;
        __bugsnagConsolePatched?: boolean;
    }
}